/**
 * Widget-instance config editor — the org-admin's control surface for one
 * instance. Edits every JSON config block and renders the install snippets:
 *
 *  - Capture level slider (0–3) with a description of what each level collects.
 *  - Allowed origins (CORS allowlist).
 *  - Masking: maskInputs toggle + maskSelectors / blockSelectors / scrubPatterns.
 *  - Feature flags: voiceAgent / textAgent / replay / implicitFiling.
 *  - Live agent: provider / model / voice / knowledge / max session.
 *  - Branding: colors, logo, launcher position/text, theme.
 *  - Install snippets for all three modes + a CSP block.
 *
 * Saves via PATCH /admin/widget-instances/:id. Local form state is initialized
 * from the fetched instance and the contract defaults.
 *
 * Visual: Claude Design "ConfigEditor" — a 196px left config nav (anchor links)
 * + a scrolling column of `config-sec` sections. The design switches sections
 * with local state, but the real screen keeps every section mounted (so the
 * whole config is saved at once) and the nav scrolls between them.
 */
import { useEffect, useId, useMemo, useState, type ReactElement, type ReactNode } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  CAPTURE_LEVELS,
  type AgentProvider,
  type BrandingConfig,
  type CaptureLevel,
  type FeatureFlags,
  type MaskingConfig,
  type UpdateWidgetInstance,
  type WidgetInstance,
  type WidgetLauncher,
  type WidgetSize,
} from '@hotline/contracts';

import { useAuth } from '../../auth/AuthContext.js';
import { useOrgScope } from '../../org/OrgScopeContext.js';
import {
  Button,
  Field,
  Input,
  Select,
  Segmented,
  Switch,
  Spinner,
  EmptyState,
  Badge,
  cn,
} from '../../components/ui/index.js';
import { WidgetPreview } from '../../components/WidgetPreview.js';
import { Icon, type IconName } from '../../components/ui/icons.js';
// ErrorBanner (wraps the shared Alert) + CopyBlock — token-based, theme-aware.
import { ErrorBanner, CopyBlock } from '../../components/ui.js';
import { McpSection } from '../../components/McpSection.js';
import { StringListEditor } from '../../components/StringListEditor.js';
import {
  canonicalizeAllowedOrigin,
  validateAllowedOrigin,
  warnAboutStoredOrigin,
} from '../../lib/allowed-origin.js';
import { useAsync, toMessage } from '../../lib/useAsync.js';
import { buildInstallSnippets } from '@hotline/integration-kit';
import { WIDGET_CDN_URL, API_BASE_URL } from '../../config.js';

/** Human-readable description of what each capture level collects. */
export const CAPTURE_LEVEL_INFO: Record<CaptureLevel, { name: string; blurb: string }> = {
  0: { name: 'L0 · Text only', blurb: 'Message/voice transcript + page URL + viewport. No extra JS.' },
  1: { name: 'L1 · Diagnostics', blurb: '+ DOM screenshot, JS errors, console error/warn.' },
  2: { name: 'L2 · Context', blurb: '+ user action timeline (last 50) and full console.' },
  3: { name: 'L3 · Replay', blurb: '+ rrweb session replay (rolling checkout).' },
};

const AGENT_PROVIDERS: AgentProvider[] = ['fake', 'openai', 'gemini'];
const POSITIONS: NonNullable<BrandingConfig['position']>[] = [
  'bottom-right',
  'bottom-left',
  'top-right',
  'top-left',
];
const THEMES: NonNullable<BrandingConfig['theme']>[] = ['light', 'dark', 'auto'];

/** Launcher looks, with human labels for the picker. */
const LAUNCHERS: { value: WidgetLauncher; label: string; blurb: string }[] = [
  { value: 'bubble', label: 'Small icon', blurb: 'A compact circular icon button.' },
  { value: 'bar', label: 'Feedback bar', blurb: 'A labelled pill button — form-forward.' },
  { value: 'anchor', label: 'Tiny anchor', blurb: 'A minimal low-profile labelled chip.' },
  { value: 'tab', label: 'Slide-out tab', blurb: 'A handle on the screen edge.' },
  { value: 'hidden', label: 'Invisible', blurb: 'No launcher — opened only by the hotkey.' },
];
const SIZES: { value: WidgetSize; label: string }[] = [
  { value: 'small', label: 'Small' },
  { value: 'medium', label: 'Medium' },
  { value: 'large', label: 'Large' },
];

/** Left config-nav entries — id anchors the matching <section>. */
const CFG_NAV: { id: string; label: string; icon: IconName }[] = [
  { id: 'general', label: 'General', icon: 'sliders' },
  { id: 'capture', label: 'Capture', icon: 'camera' },
  { id: 'origins', label: 'Allowed origins', icon: 'globe' },
  { id: 'masking', label: 'Masking & privacy', icon: 'shield' },
  { id: 'features', label: 'Feature flags', icon: 'flag' },
  { id: 'agent', label: 'Live agent', icon: 'mic' },
  { id: 'branding', label: 'Branding', icon: 'type' },
  { id: 'mcp', label: 'AI integration', icon: 'code' },
  { id: 'install', label: 'Manual install', icon: 'code' },
];

/** What a project-scoped MCP token gets an agent, in one sentence. */
const MCP_PROJECT_BLURB =
  'Connect your AI coding agent and it can do the whole integration itself: read the ' +
  'docs, fetch exact embed code, diagnose origins, add missing ones, run a live ' +
  'end-to-end test against this project, and report recent feedback. One token is the ' +
  'entire configuration.';

/** Editable form shape mirrors the patchable instance fields. */
interface FormState {
  name: string;
  captureLevel: CaptureLevel;
  allowedOrigins: string[];
  masking: Required<Pick<MaskingConfig, 'maskInputs' | 'maskSelectors' | 'blockSelectors' | 'scrubPatterns'>>;
  features: FeatureFlags;
  agent: {
    provider: AgentProvider;
    model: string;
    voice: string;
    knowledgeEnabled: boolean;
    maxSessionMs: number;
  };
  branding: {
    launcher: WidgetLauncher;
    size: WidgetSize;
    primaryColor: string;
    accentColor: string;
    logoUrl: string;
    position: NonNullable<BrandingConfig['position']>;
    draggable: boolean;
    hotkey: string;
    launcherText: string;
    theme: NonNullable<BrandingConfig['theme']>;
  };
  status: WidgetInstance['status'];
}

function toForm(wi: WidgetInstance): FormState {
  return {
    name: wi.name,
    captureLevel: wi.captureLevel,
    allowedOrigins: [...wi.allowedOrigins],
    masking: {
      maskInputs: wi.masking.maskInputs,
      maskSelectors: [...wi.masking.maskSelectors],
      blockSelectors: [...wi.masking.blockSelectors],
      scrubPatterns: [...wi.masking.scrubPatterns],
    },
    features: { ...wi.features },
    agent: {
      provider: wi.agent.provider,
      model: wi.agent.model ?? '',
      voice: wi.agent.voice ?? '',
      knowledgeEnabled: wi.agent.knowledgeEnabled,
      maxSessionMs: wi.agent.maxSessionMs,
    },
    branding: {
      launcher: wi.branding.launcher,
      size: wi.branding.size,
      primaryColor: wi.branding.primaryColor ?? '',
      accentColor: wi.branding.accentColor ?? '',
      logoUrl: wi.branding.logoUrl ?? '',
      position: wi.branding.position,
      draggable: wi.branding.draggable,
      hotkey: wi.branding.hotkey ?? '',
      launcherText: wi.branding.launcherText ?? '',
      theme: wi.branding.theme,
    },
    status: wi.status,
  };
}

/** Build the PATCH body from the edited form (dropping empty optional strings). */
export function toUpdatePayload(form: FormState): UpdateWidgetInstance {
  return {
    name: form.name,
    captureLevel: form.captureLevel,
    allowedOrigins: form.allowedOrigins,
    masking: form.masking,
    features: form.features,
    agent: {
      provider: form.agent.provider,
      model: form.agent.model,
      voice: form.agent.voice || undefined,
      knowledgeEnabled: form.agent.knowledgeEnabled,
      maxSessionMs: form.agent.maxSessionMs,
    },
    branding: {
      launcher: form.branding.launcher,
      size: form.branding.size,
      primaryColor: form.branding.primaryColor || undefined,
      accentColor: form.branding.accentColor || undefined,
      logoUrl: form.branding.logoUrl || undefined,
      position: form.branding.position,
      draggable: form.branding.draggable,
      hotkey: form.branding.hotkey || undefined,
      launcherText: form.branding.launcherText || undefined,
      theme: form.branding.theme,
    },
    status: form.status,
  };
}

/**
 * A grouped config block (Claude Design `.config-sec`). Keeps native
 * `<fieldset>`/`<legend>` semantics (so the legend labels the group for
 * assistive tech) — the legend is rendered as the design's `config-sec-h`
 * heading with an optional description.
 */
function EditorSection({
  id,
  legend,
  description,
  children,
}: {
  id: string;
  legend: string;
  description?: ReactNode;
  children: ReactNode;
}): ReactElement {
  return (
    <fieldset
      id={id}
      className="mx-auto max-w-[720px] scroll-mt-20 border-b border-border px-1 py-[22px] last:border-b-0 sm:px-7"
    >
      <legend className="float-left mb-[18px] w-full">
        <h2 className="text-base font-bold text-text">{legend}</h2>
        {description ? (
          <p className="mt-[3px] text-[12.5px] leading-relaxed text-muted">{description}</p>
        ) : null}
      </legend>
      <div className="clear-both flex flex-col">{children}</div>
    </fieldset>
  );
}

/**
 * A config row (Claude Design `.cfg-row`): a title + description on the left and
 * a control slot on the right, separated by a top border. When `controlId` is
 * given the title acts as the control's accessible label (`aria-labelledby`).
 */
function CfgRow({
  title,
  description,
  titleId,
  children,
}: {
  title: string;
  description?: string;
  titleId?: string;
  children: ReactNode;
}): ReactElement {
  return (
    <div className="flex items-start justify-between gap-5 border-t border-border py-3.5 first:border-t-0">
      <div className="flex-1">
        <div id={titleId} className="text-[13.5px] font-semibold text-text">
          {title}
        </div>
        {description ? (
          <div className="mt-0.5 text-xs leading-relaxed text-muted">{description}</div>
        ) : null}
      </div>
      <div className="flex shrink-0 items-center">{children}</div>
    </div>
  );
}

/** A boolean toggle row: a Switch whose accessible name is the row title. */
function ToggleRow({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}): ReactElement {
  const titleId = useId();
  return (
    <CfgRow title={label} description={description} titleId={titleId}>
      <Switch
        aria-labelledby={titleId}
        checked={checked}
        onCheckedChange={(v) => onChange(v === true)}
      />
    </CfgRow>
  );
}

export default function ConfigEditorPage(): ReactElement {
  const { instanceId = '' } = useParams();
  const { client } = useAuth();
  const { query } = useOrgScope();

  const instance = useAsync<WidgetInstance>(
    () => client.api.getWidgetInstance(instanceId, query),
    [client, instanceId, query.orgId],
  );

  // Declared with the other hooks, NOT inline in the JSX below: the sections
  // render after an early `return <Spinner/>`, so a hook called down there runs
  // conditionally and React tears the tree down on the next render.
  const mcpApi = useMemo(
    () => ({
      scopeKey: `instance:${instanceId}:${query.orgId ?? ''}`,
      list: () => client.api.listMcpTokens(instanceId, query),
      create: (label: string) => client.api.createMcpToken(instanceId, label, query),
      revoke: (tokenId: string) => client.api.revokeMcpToken(instanceId, tokenId, query),
    }),
    [client, instanceId, query.orgId],
  );

  const [form, setForm] = useState<FormState | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    if (instance.data) setForm(toForm(instance.data));
  }, [instance.data]);

  const snippets = useMemo(
    () =>
      instance.data
        ? buildInstallSnippets({
            publicKey: instance.data.publicKey,
            cdnUrl: WIDGET_CDN_URL,
            apiUrl: API_BASE_URL,
            // Carried so the AI-agent prompt can name the instance it is wiring
            // up, and so the verify command uses a real allowlisted origin. A
            // prompt that names the instance is what catches a site embedding
            // the wrong key — the failure mode the diagnose endpoint exists for.
            instanceName: instance.data.name,
            allowedOrigins: instance.data.allowedOrigins,
          })
        : [],
    [instance.data],
  );

  if (instance.loading) return <Spinner label="Loading widget…" />;
  if (instance.error) return <ErrorBanner message={instance.error} />;
  if (!instance.data || !form)
    return (
      <EmptyState
        title="Instance not found"
        description="This widget instance does not exist or you no longer have access to it."
      />
    );

  const wi = instance.data;

  const patch = <K extends keyof FormState>(key: K, value: FormState[K]): void => {
    setForm((f) => (f ? { ...f, [key]: value } : f));
  };

  const save = async (): Promise<void> => {
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await client.api.updateWidgetInstance(
        instanceId,
        toUpdatePayload(form),
        query,
      );
      instance.setData(updated);
      setSavedAt(Date.now());
    } catch (err) {
      setSaveError(toMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="flex flex-col gap-4">
      {/* ── Page header ─────────────────────────────────────────── */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-1">
          <Link
            to="/org/instances"
            className="inline-flex w-fit items-center gap-1.5 text-[13px] font-semibold text-muted transition-colors hover:text-text"
          >
            <Icon.arrowLeft className="size-3.5" />
            Instances
          </Link>
          <h1 className="truncate text-xl font-bold tracking-tight text-text">{wi.name}</h1>
          <code className="font-mono text-xs text-muted">{wi.publicKey}</code>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {savedAt ? (
            <Badge tone="ok">
              <Icon.check />
              Saved
            </Badge>
          ) : null}
          <Button type="button" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </div>

      {saveError ? <ErrorBanner message={saveError} /> : null}

      {/* ── Two-column config shell: 196px nav + scrolling sections ─ */}
      <div className="flex min-h-0 gap-0 overflow-hidden rounded-xl border border-border bg-surface shadow-card">
        <nav
          aria-label="Configuration sections"
          className="hidden w-[196px] shrink-0 flex-col gap-0.5 self-start border-r border-border p-4 md:sticky md:top-[70px] md:flex"
        >
          <div className="px-[9px] pb-1.5 pt-0.5 text-[10.5px] font-extrabold uppercase tracking-[0.12em] text-faint">
            Configuration
          </div>
          {CFG_NAV.map((n) => {
            const I = Icon[n.icon];
            return (
              <a
                key={n.id}
                href={`#${n.id}`}
                className={cn(
                  'flex items-center gap-2.5 whitespace-nowrap rounded-md px-[9px] py-[7px] text-[13px] font-semibold text-muted transition-colors',
                  'hover:bg-surface-hover hover:text-text',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                )}
              >
                <I className="size-4 shrink-0 opacity-80" />
                {n.label}
              </a>
            );
          })}
        </nav>

        <div className="min-w-0 flex-1 py-2">
          {/* ── General ─────────────────────────────────────────── */}
          <EditorSection
            id="general"
            legend="General"
            description="Name and lifecycle of this widget instance."
          >
            <Field label="Name" htmlFor="wi-name" className="py-3.5">
              <Input
                id="wi-name"
                value={form.name}
                onChange={(e) => patch('name', e.target.value)}
              />
            </Field>
            <Field label="Status" htmlFor="wi-status" className="py-3.5">
              <Select
                id="wi-status"
                className="max-w-[200px]"
                value={form.status}
                onChange={(e) => patch('status', e.target.value as WidgetInstance['status'])}
              >
                <option value="active">active</option>
                <option value="disabled">disabled</option>
              </Select>
            </Field>
          </EditorSection>

          {/* ── Capture level ───────────────────────────────────── */}
          <EditorSection
            id="capture"
            legend="Capture level"
            description="What Hotline collects automatically with each report."
          >
            <div className="flex flex-col gap-3 py-3.5">
              {/* Native range input (no DS slider primitive yet); only restyled. */}
              <input
                type="range"
                aria-label="Capture level"
                min={0}
                max={3}
                step={1}
                value={form.captureLevel}
                onChange={(e) =>
                  patch('captureLevel', Number(e.target.value) as CaptureLevel)
                }
                list="capture-levels"
                className="h-1.5 w-full max-w-xs cursor-pointer appearance-none rounded-full bg-surface-3 accent-[var(--color-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              />
              <datalist id="capture-levels">
                {CAPTURE_LEVELS.map((l) => (
                  <option key={l} value={l} />
                ))}
              </datalist>
              <div>
                <p
                  className="text-[13.5px] font-semibold text-text"
                  data-level={form.captureLevel}
                >
                  {CAPTURE_LEVEL_INFO[form.captureLevel].name}
                </p>
                <p className="mt-0.5 text-xs leading-relaxed text-muted">
                  {CAPTURE_LEVEL_INFO[form.captureLevel].blurb}
                </p>
              </div>
            </div>
          </EditorSection>

          {/* ── Allowed origins ─────────────────────────────────── */}
          <EditorSection
            id="origins"
            legend="Allowed origins"
            description={
              <>
                The widget only loads + ingests from these exact origins (CORS is
                echoed per-request, never <code className="font-mono">*</code>).
              </>
            }
          >
            <div className="py-3.5">
              {form.allowedOrigins.length === 0 ? (
                <p
                  role="alert"
                  className="mb-3 rounded-md border border-warn/40 bg-warn-soft px-3 py-2 text-[12px] font-semibold text-warn"
                >
                  No origins listed — the widget is blocked on every site. Add the
                  origin(s) it is embedded on, e.g. https://app.example.com.
                </p>
              ) : null}
              <StringListEditor
                label="Add allowed origin"
                placeholder="https://app.example.com"
                values={form.allowedOrigins}
                onChange={(v) => patch('allowedOrigins', v)}
                validate={validateAllowedOrigin}
                normalize={canonicalizeAllowedOrigin}
                warn={warnAboutStoredOrigin}
              />
            </div>
          </EditorSection>

          {/* ── Masking ─────────────────────────────────────────── */}
          <EditorSection
            id="masking"
            legend="PII masking"
            description="Nothing sensitive leaves the visitor's browser."
          >
            <ToggleRow
              label="Mask all input/textarea values by default"
              description="Replace text in form fields with blocks before capture."
              checked={form.masking.maskInputs}
              onChange={(v) => patch('masking', { ...form.masking, maskInputs: v })}
            />

            <Field
              label="Mask selectors"
              hint="Text/values matched by these CSS selectors are masked."
              className="border-t border-border py-3.5"
            >
              <StringListEditor
                label="Add mask selector"
                placeholder=".credit-card, #ssn"
                values={form.masking.maskSelectors}
                onChange={(v) => patch('masking', { ...form.masking, maskSelectors: v })}
              />
            </Field>

            <Field
              label="Block selectors"
              hint="Elements removed entirely from screenshots/replay."
              className="border-t border-border py-3.5"
            >
              <StringListEditor
                label="Add block selector"
                placeholder=".sensitive-panel"
                values={form.masking.blockSelectors}
                onChange={(v) => patch('masking', { ...form.masking, blockSelectors: v })}
              />
            </Field>

            <Field
              label="Scrub patterns"
              hint="Regex sources scrubbed from captured text server-side (email/card/phone)."
              className="border-t border-border py-3.5"
            >
              <StringListEditor
                label="Add scrub pattern"
                placeholder="\\d{16}"
                values={form.masking.scrubPatterns}
                onChange={(v) => patch('masking', { ...form.masking, scrubPatterns: v })}
              />
            </Field>
          </EditorSection>

          {/* ── Feature flags ───────────────────────────────────── */}
          <EditorSection
            id="features"
            legend="Features"
            description="Toggle capabilities for this widget."
          >
            {(
              [
                ['voiceAgent', 'Voice agent', 'Offer the live voice agent in the widget.'],
                ['textAgent', 'Text agent', 'Offer the live text agent in the widget.'],
                ['replay', 'Session replay (rrweb)', 'Record a masked DOM replay with each report.'],
                ['implicitFiling', 'Implicit filing', 'Let the agent file a ticket without an explicit submit.'],
              ] as [keyof FeatureFlags, string, string][]
            ).map(([key, label, description]) => (
              <ToggleRow
                key={key}
                label={label}
                description={description}
                checked={form.features[key]}
                onChange={(v) => patch('features', { ...form.features, [key]: v })}
              />
            ))}
          </EditorSection>

          {/* ── Live agent ──────────────────────────────────────── */}
          <EditorSection
            id="agent"
            legend="Live agent"
            description="The live AI agent that talks visitors through their feedback."
          >
            <Field label="Provider" htmlFor="agent-provider" className="py-3.5">
              <Select
                id="agent-provider"
                className="max-w-[200px]"
                value={form.agent.provider}
                onChange={(e) =>
                  patch('agent', { ...form.agent, provider: e.target.value as AgentProvider })
                }
              >
                {AGENT_PROVIDERS.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </Select>
            </Field>
            <Field
              label="Model"
              htmlFor="agent-model"
              hint="Leave blank for the local Fake provider."
              className="border-t border-border py-3.5"
            >
              <Input
                id="agent-model"
                value={form.agent.model}
                onChange={(e) => patch('agent', { ...form.agent, model: e.target.value })}
              />
            </Field>
            <Field
              label="Voice"
              htmlFor="agent-voice"
              className="border-t border-border py-3.5"
            >
              <Input
                id="agent-voice"
                value={form.agent.voice}
                onChange={(e) => patch('agent', { ...form.agent, voice: e.target.value })}
              />
            </Field>
            <ToggleRow
              label="Enable site-knowledge grounding"
              description="Ground the agent in your indexed site content."
              checked={form.agent.knowledgeEnabled}
              onChange={(v) => patch('agent', { ...form.agent, knowledgeEnabled: v })}
            />
            <Field
              label="Max session (ms)"
              htmlFor="agent-max"
              className="border-t border-border py-3.5"
            >
              <Input
                id="agent-max"
                type="number"
                min={1000}
                className="max-w-[200px]"
                value={form.agent.maxSessionMs}
                onChange={(e) =>
                  patch('agent', { ...form.agent, maxSessionMs: Number(e.target.value) })
                }
              />
            </Field>
          </EditorSection>

          {/* ── Branding ────────────────────────────────────────── */}
          <EditorSection
            id="branding"
            legend="Branding"
            description={
              <>
                Match the widget to the host site. These map onto the widget's{' '}
                <span className="font-mono">--ht-primary</span> /{' '}
                <span className="font-mono">--ht-accent</span> tokens.
              </>
            }
          >
            {/* Live, faithful preview — reacts to every control below. */}
            <div className="pb-3.5">
              <WidgetPreview
                launcher={form.branding.launcher}
                size={form.branding.size}
                position={form.branding.position}
                primaryColor={form.branding.primaryColor}
                accentColor={form.branding.accentColor}
                launcherText={form.branding.launcherText}
                theme={form.branding.theme}
                draggable={form.branding.draggable}
                hotkey={form.branding.hotkey}
              />
            </div>

            {/* Look — the five launcher appearances. */}
            <div className="border-t border-border py-3.5">
              <div className="mb-2 text-[13.5px] font-semibold text-text">Look</div>
              <div className="overflow-x-auto">
                <Segmented
                  aria-label="Launcher look"
                  value={form.branding.launcher}
                  onValueChange={(v) =>
                    patch('branding', { ...form.branding, launcher: v as WidgetLauncher })
                  }
                  options={LAUNCHERS.map((l) => ({ value: l.value, label: l.label }))}
                />
              </div>
              <p className="mt-1.5 text-xs leading-relaxed text-muted">
                {LAUNCHERS.find((l) => l.value === form.branding.launcher)?.blurb}
              </p>
            </div>

            {/* Size. */}
            <div className="border-t border-border py-3.5">
              <div className="mb-2 text-[13.5px] font-semibold text-text">Size</div>
              <Segmented
                aria-label="Widget size"
                value={form.branding.size}
                onValueChange={(v) =>
                  patch('branding', { ...form.branding, size: v as WidgetSize })
                }
                options={SIZES.map((s) => ({ value: s.value, label: s.label }))}
              />
            </div>

            <Field label="Primary color" htmlFor="brand-primary" className="border-t border-border py-3.5">
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  aria-label="Primary color swatch"
                  value={
                    /^#[0-9a-fA-F]{6}$/.test(form.branding.primaryColor)
                      ? form.branding.primaryColor
                      : '#5b5bd6'
                  }
                  onChange={(e) =>
                    patch('branding', { ...form.branding, primaryColor: e.target.value })
                  }
                  className="size-9 shrink-0 cursor-pointer rounded border border-border bg-transparent p-0.5"
                />
                <Input
                  id="brand-primary"
                  placeholder="#5b8cff"
                  className="max-w-[200px]"
                  value={form.branding.primaryColor}
                  onChange={(e) =>
                    patch('branding', { ...form.branding, primaryColor: e.target.value })
                  }
                />
              </div>
            </Field>
            <Field
              label="Accent color"
              htmlFor="brand-accent"
              className="border-t border-border py-3.5"
            >
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  aria-label="Accent color swatch"
                  value={
                    /^#[0-9a-fA-F]{6}$/.test(form.branding.accentColor)
                      ? form.branding.accentColor
                      : '#8e8ef5'
                  }
                  onChange={(e) =>
                    patch('branding', { ...form.branding, accentColor: e.target.value })
                  }
                  className="size-9 shrink-0 cursor-pointer rounded border border-border bg-transparent p-0.5"
                />
                <Input
                  id="brand-accent"
                  placeholder="#22d3ee"
                  className="max-w-[200px]"
                  value={form.branding.accentColor}
                  onChange={(e) =>
                    patch('branding', { ...form.branding, accentColor: e.target.value })
                  }
                />
              </div>
            </Field>

            <ToggleRow
              label="Draggable"
              description="Let visitors drag the launcher to reposition it (saved in their browser)."
              checked={form.branding.draggable}
              onChange={(v) => patch('branding', { ...form.branding, draggable: v })}
            />

            <Field
              label="Keyboard shortcut"
              htmlFor="brand-hotkey"
              hint={'Opens the widget, e.g. "mod+shift+f" (mod = ⌘ on macOS, Ctrl elsewhere). Required for the Invisible look.'}
              className="border-t border-border py-3.5"
            >
              <Input
                id="brand-hotkey"
                placeholder="mod+shift+f"
                className="max-w-[240px]"
                value={form.branding.hotkey}
                onChange={(e) =>
                  patch('branding', { ...form.branding, hotkey: e.target.value })
                }
              />
            </Field>

            <Field
              label="Logo URL"
              htmlFor="brand-logo"
              className="border-t border-border py-3.5"
            >
              <Input
                id="brand-logo"
                placeholder="https://…/logo.svg"
                value={form.branding.logoUrl}
                onChange={(e) =>
                  patch('branding', { ...form.branding, logoUrl: e.target.value })
                }
              />
            </Field>
            <Field
              label="Starting position"
              htmlFor="brand-position"
              className="border-t border-border py-3.5"
            >
              <Select
                id="brand-position"
                className="max-w-[200px]"
                value={form.branding.position}
                onChange={(e) =>
                  patch('branding', {
                    ...form.branding,
                    position: e.target.value as FormState['branding']['position'],
                  })
                }
              >
                {POSITIONS.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </Select>
            </Field>
            <Field
              label="Launcher text"
              htmlFor="brand-launcher"
              className="border-t border-border py-3.5"
            >
              <Input
                id="brand-launcher"
                placeholder="Feedback"
                value={form.branding.launcherText}
                onChange={(e) =>
                  patch('branding', { ...form.branding, launcherText: e.target.value })
                }
              />
            </Field>
            <Field label="Theme" htmlFor="brand-theme" className="border-t border-border py-3.5">
              <Select
                id="brand-theme"
                className="max-w-[200px]"
                value={form.branding.theme}
                onChange={(e) =>
                  patch('branding', {
                    ...form.branding,
                    theme: e.target.value as FormState['branding']['theme'],
                  })
                }
              >
                {THEMES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </Select>
            </Field>
          </EditorSection>

          {/* ── Install snippets ────────────────────────────────── */}
          {/* ── AI integration (MCP) — the primary path ────────── */}
          <EditorSection
            id="mcp"
            legend="AI integration (MCP)"
            description="Hand your AI coding agent one connection string and it does the rest — including a live end-to-end test."
          >
            <McpSection blurb={MCP_PROJECT_BLURB} api={mcpApi} />
          </EditorSection>

          <EditorSection
            id="install"
            legend="Manual install"
            description="Drop these snippets into your site to load the widget."
          >
            <div className="flex flex-col gap-6 py-3.5">
              {snippets.map((s) => (
                <div key={s.id} className="flex flex-col gap-2">
                  <h3 className="text-[13.5px] font-semibold text-text">{s.label}</h3>
                  <p className="text-xs text-muted">{s.description}</p>
                  <CopyBlock code={s.code} language={s.language} />
                </div>
              ))}
            </div>
          </EditorSection>
        </div>
      </div>
    </section>
  );
}
